import type { RouteObject } from 'react-router';
import MainLayout from '~/ui/layout/MainLayout';
import { LessonPackagesPage } from '~/pages/lessons/LessonPackagesPage';
import { LessonPurchasePage } from '~/pages/lessons/LessonPurchasePage';
import { LessonPurchaseSuccessPage } from '~/pages/lessons/LessonPurchaseSuccessPage';
import { MyLessonsPage } from '~/pages/lessons/MyLessonsPage';
import { MyLessonsRedirect } from '~/pages/lessons/MyLessonsRedirect';
import { LessonFlowPage } from '~/pages/lessons';
import { BookingConfirmationPage } from '~/pages/booking/BookingConfirmationPage';
import { BookingProvider } from '~/services/internalApi/booking';
import { LessonFlowProvider } from '~/services/internalApi/lessons';

/**
 * Lesson Package Routes (BUT-841)
 *
 * Layout: MainLayout
 * Auth Guard: none applied here — GuardedLayout (club-id + tenant-handle) is
 * the only route-level gate; the catalogue is guest-browsable by design, and
 * the purchase page enforces its own in-page sign-in gate (BUT-841),
 * which is what lets a guest browse, pick, sign in, and return to the same
 * package.
 *
 * Route paths are fixed by LessonsCatalogue's and LessonPurchasePage's own
 * `navigate`/`buildUrl` calls — do not rename without updating both.
 *
 * BUT-842/843 append children here (a holdings route, `book`/`book/:lessonId`)
 * rather than creating a second `/lessons` parent.
 *
 * BUT-842: holdings moved to `/plans` (own MainLayout entry, same guard
 * nesting as `/lessons`). `/lessons/my` is now a legacy redirect (see
 * `MyLessonsRedirect`) — preserves search + hash so tenant-handle/club-id
 * survive the bounce.
 *
 * `book` is the 3-step lesson flow (steps switch inside one route; the time
 * and bay steps are scaffolded behind lessonAvailabilitySource) and
 * `confirmed/:bookingId` the lesson variant of the shared confirmation page.
 * `BookingProvider persist={false}`: WaiverSection reads consent from the
 * booking context, and lesson selections must not leak into the standard
 * flow's restored sessionStorage state.
 */
export const lessonRoutes: RouteObject[] = [
  {
    path: '/lessons',
    element: <MainLayout />,
    children: [
      { index: true, element: <LessonPackagesPage /> },
      { path: 'purchase', element: <LessonPurchasePage /> },
      { path: 'purchased/:userLessonId', element: <LessonPurchaseSuccessPage /> },
      { path: 'my', element: <MyLessonsRedirect /> },
      {
        path: 'book',
        element: (
          <BookingProvider persist={false}>
            <LessonFlowProvider>
              <LessonFlowPage />
            </LessonFlowProvider>
          </BookingProvider>
        ),
      },
      { path: 'confirmed/:bookingId', element: <BookingConfirmationPage variant="lesson" /> },
    ],
  },
  {
    path: '/plans',
    element: <MainLayout />,
    children: [{ index: true, element: <MyLessonsPage /> }],
  },
];
